import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { IntlProvider } from "react-intl";
import posthog from "posthog-js";
import "bootstrap/dist/css/bootstrap.min.css";

import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

import router from "./routes/RouterConfig";
import { store } from "./store";
import config from "./config";
import "./index.scss";
import { RootState } from "./types/state";
import { PosthogContext } from "./posthog/PosthogContext";
import { getEnvironmentFromDomain, getDeviceModel } from "./utils/helperFunctions";
import webLogger from "./tracing";

const SENTRY_DSN = config.SENTRY_DSN
const SENTRY_ENVIRONMENT = config.SENTRY_ENVIRONMENT
const SENTRY_RELEASE = config.SENTRY_RELEASE

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    integrations: [new BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}


const env = getEnvironmentFromDomain();
const posthogKey =
  env === "prod" ? config.POSTHOG_KEY_PROD : config.POSTHOG_KEY;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: config.POSTHOG_API_HOST,
  });
}

getDeviceModel();

const shouldStartNewSession = (): boolean => {
  try {
    console.log('URL-based navigation detection...')

    const currentUrl = window.location.href
    const lastUrl = sessionStorage.getItem('last_page_url')

    if (!lastUrl) {
      console.log('No previous URL - fresh session')
      sessionStorage.setItem('last_page_url', currentUrl)
      return true
    }

    if (currentUrl === lastUrl) {
      console.log('Same URL as before - page reload detected')
      return true
    } else {
      console.log('Different URL - internal navigation')
      sessionStorage.setItem('last_page_url', currentUrl)
      return false
    }
  } catch (error) {
    console.warn('URL detection failed:', error)
    console.log('Defaulting to fresh session')
    return true
  }
}

const setupUrlTracking = () => {
  try {
    const updateLastUrl = () => {
      try {
        sessionStorage.setItem('last_page_url', window.location.href)
      } catch (e) {
        // Ignore storage errors
      }
    }

    window.addEventListener('beforeunload', updateLastUrl)
    window.addEventListener('pagehide', updateLastUrl)

    const handleVisibilityChange = () => {
      if (document.hidden) {
        updateLastUrl()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
  } catch (error) {
    console.warn('URL tracking setup failed:', error)
  }
}

setupUrlTracking()

const needsNewSession = shouldStartNewSession()

console.log('Session decision:', needsNewSession ? 'NEW SESSION' : 'CONTINUE SESSION')

if (needsNewSession) {
  webLogger.startNewSession()
  console.log('Started new session')
} else {
  console.log('Continuing existing session')
}

const App = () => {
  const theme = useSelector((state: RootState) => state.theme);

  return (
    <React.StrictMode>
      <PosthogContext.Provider value={{ posthog }}>
        <IntlProvider messages={theme.messages} locale="en" defaultLocale="en">
          <RouterProvider router={router} />
        </IntlProvider>
      </PosthogContext.Provider>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <Provider store={store}>
    <App />
  </Provider>
);
